import React, { useEffect } from 'react';
import { Deal, ProductSubscription, ProductSubscriptionSuccessFeeTier, SuccessFeeTier, SuccessFeeTypeMap } from '../../shared/models';
import { CerebroDrawer, CerebroHeading, CerebroSecondaryButton, CerebroText } from '../../shared/components';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Skeleton, Alert, Box } from '@mui/material';
import { NumberUtils } from '../../shared/utils';
import { SuccessFeeTierFormDialog } from './SuccessFeeTierFormDialog';
import { DeleteSuccessFeeTierDialog } from './DeleteSuccessFeeTierDialog';
import { EmptyGuid } from '../../shared/constants';
import { SuccessFeeTierActionButton } from './SuccessFeeTierActionButton';
import { useSubscriptionSuccessFeeTiersQuery } from '../../shared/hooks';
import { validateTierStructure } from '../../shared/utils/tiers';

interface SubscriptionSuccessFeeTiersDrawerProps {
	onClose: () => void;
	subscription: ProductSubscription | null;
	deal: Deal;
}

export const SubscriptionSuccessFeeTiersDrawer: React.FC<SubscriptionSuccessFeeTiersDrawerProps> = ({
	subscription,
	deal,
	onClose,
}) => {
	const ref = React.useRef<HTMLDivElement>(null);
	const [tierForm, setTierForm] = React.useState<ProductSubscriptionSuccessFeeTier | null>(null);
	const [tierDelete, setTierDelete] = React.useState<ProductSubscriptionSuccessFeeTier | null>(null);
	const tiersQuery = useSubscriptionSuccessFeeTiersQuery(deal.guid, subscription?.guid, {
		enabled: subscription != null,
		retry: false,
	});
	const isFetching = tiersQuery.isLoading || tiersQuery.isFetching;

	const onCloseDialogs = () => {
		setTierForm(null);
		setTierDelete(null);
	};

	const createNewSuccessFeeTier = () => {
		setTierForm({
			guid: EmptyGuid,
			lowerFundingAmount: 0,
			basisPoints: 0,
		} as ProductSubscriptionSuccessFeeTier);
	};

	return (
		<CerebroDrawer ref={ref} open={subscription != null} onClose={onClose}>
			<CerebroHeading>Update Success Fee Tiers</CerebroHeading>

			{subscription?.successFeeType == SuccessFeeTypeMap.Tiered && !validateTierStructure(tiersQuery.data as SuccessFeeTier[]) && (
				<Alert severity="info">Success fee tiers are invalid, please correct to view contract document.</Alert>
			)}

			<CerebroSecondaryButton onClick={() => createNewSuccessFeeTier()} sx={{ marginBottom: 2, marginTop: 3 }}>
				Add Tier
			</CerebroSecondaryButton>

			<TableContainer sx={{ marginBottom: 8 }}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Lower Funding Amount</TableCell>
							<TableCell>Upper Funding Amount</TableCell>
							<TableCell>Bps</TableCell>
							<TableCell></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{isFetching && (
							<TableRow>
								<TableCell colSpan={4}>
									<Skeleton height={60} />
								</TableCell>
							</TableRow>
						)}

						{tiersQuery.isSuccess && (
							<>
								{tiersQuery.data.map(tier => {
									return (
										<TableRow key={tier.guid}>
											<TableCell>{NumberUtils.FormatCurrency(tier.lowerFundingAmount)}</TableCell>
											<TableCell>{NumberUtils.FormatCurrency(tier.upperFundingAmount)}</TableCell>
											<TableCell>{tier.basisPoints}</TableCell>
											<TableCell>
												<SuccessFeeTierActionButton
													tier={tier}
													onEdit={setTierForm}
													onDelete={setTierDelete}
												/>
											</TableCell>
										</TableRow>
									);
								})}
								{tiersQuery.data.length == 0 && (
									<TableRow>
										<TableCell colSpan={4}>
											No success fee tiers are currently associated to the subscription.
										</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Box>
				<CerebroText>Tier Validation Rules</CerebroText>
				<ul style={{lineHeight: 2}}>
					<li>
						<CerebroText>If success fee type is tiered, there must be at least two tiers</CerebroText>
					</li>
					<li>
						<CerebroText>First tier must have a $0 lower funding amount</CerebroText>
					</li>
					<li>
						<CerebroText>Middle Tiers</CerebroText>
					</li>
					<ul>
						<li>
							<CerebroText>Must have a lower and upper funding amount</CerebroText>
						</li>
						<li>
							{' '}
							<CerebroText>Lower funding amount must be less than upper funding amount</CerebroText>
						</li>
						<li>
							<CerebroText>
								Lower funding amount must be equal to the previous tier upper funding amount
							</CerebroText>
						</li>
					</ul>
					<li>
						<CerebroText>The last tier must have no upper funding amount</CerebroText>
					</li>
				</ul>
			</Box>

			<SuccessFeeTierFormDialog
				subscription={subscription}
				tier={tierForm}
				dealGuid={deal.guid}
				close={onCloseDialogs}
			/>
			<DeleteSuccessFeeTierDialog
				subscription={subscription}
				tier={tierDelete}
				dealGuid={deal.guid}
				close={onCloseDialogs}
			/>
		</CerebroDrawer>
	);
};
